/**
 * Modul "Sanktionssystem": Nachfolger des entfernten Moduls "Sanktionen"
 * (Migration 0044) mit automatischer Discord-Synchronisierung aus einem
 * eigenen Channel (siehe backend/src/sanctionssystemSync.ts). Zugriff über
 * die Berechtigung "sanctionssystem.view" (App.tsx, Sidebar.tsx/
 * MobileDrawer.tsx), ab Werk President/VizePresident/Secretary/Treasurer/
 * Phantom. Datei heißt bewusst SanctionssystemPage, analog zu RoutePage, um
 * Namenskollisionen zu vermeiden.
 *
 * "Bezahlte Sanktionen" (eigene Unteransicht, analog zum Reiter-Muster von
 * UsersHub.tsx): als "bezahlt" markierte Sanktionen werden aus der normalen
 * Liste herausgenommen ("verschoben") und erscheinen ausschließlich in
 * dieser separaten Ansicht - bewusst KEINE eigene Route/eigener Endpunkt,
 * sondern eine reine Client-seitige Filterung derselben ohnehin bereits
 * geladenen Daten (GET /api/sanctionssystem liefert schon heute ALLE
 * Einträge an jeden Benutzer mit "sanctionssystem.view" - kein neuer
 * Datenzugriff, nur eine andere Darstellung). Die Umschaltfläche dafür ist
 * für alle Rollen mit Zugriff auf dieses Modul sichtbar (die Route ist
 * bereits über "sanctionssystem.view" geschützt, das "Member" ab Werk nicht
 * besitzt - siehe Migration 0049 - daher entspricht dies "alle Rollen außer
 * Member").
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { Card } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { Modal } from "../../components/ui/Modal";
import { Spinner, EmptyState } from "../../components/ui/Spinner";
import { formatDateTime, parseServerDate } from "../../utils/formatDate";
import type { SanctionssystemEntry, SanctionssystemSyncStatus } from "../../types";

const STATUS_LABELS: Record<SanctionssystemEntry["status"], string> = {
  open: "Offen", paid: "Bezahlt", unpaid: "Nicht bezahlt",
};
const STATUS_TONE: Record<SanctionssystemEntry["status"], "success" | "danger" | "warning"> = {
  open: "warning", paid: "success", unpaid: "danger",
};

const PAYMENT_DEADLINE_DAYS = 7;
const PAID_CLEANUP_DAYS = 12;

/** "Zu bezahlen bis": immer Erstellungsdatum + 7 Tage (siehe Anforderung) -
 *  rein abgeleitet, kein eigenes gespeichertes Feld (analog zu Sanctions.tsx),
 *  damit dies automatisch auch für alle bereits bestehenden Sanktionen gilt. */
function dueDate(createdAt: string): Date {
  const d = parseServerDate(createdAt);
  d.setDate(d.getDate() + PAYMENT_DEADLINE_DAYS);
  return d;
}

/** "Wird gelöscht am": paid_at + 12 Tage (siehe Migration 0060, backend/src/
 *  sanctionssystemSync.ts runSanctionssystemPaidCleanup()) - die tatsächliche
 *  Löschung übernimmt derselbe minütliche Hintergrundauftrag serverseitig,
 *  dies ist nur die informative Anzeige des berechneten Zeitpunkts. */
function deletionDate(paidAt: string): Date {
  const d = parseServerDate(paidAt);
  d.setDate(d.getDate() + PAID_CLEANUP_DAYS);
  return d;
}

export default function SanctionssystemPage() {
  const [entries, setEntries] = useState<SanctionssystemEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<SanctionssystemEntry | null | undefined>(undefined);
  const [view, setView] = useState<"open" | "paid">("open");
  const showPaidView = view === "paid";
  const visibleEntries = entries.filter((e) => (showPaidView ? e.status === "paid" : e.status !== "paid"));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setEntries(await api.get<SanctionssystemEntry[]>("/api/sanctionssystem"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(entry: SanctionssystemEntry) {
    if (!confirm(`Möchtest du die Sanktion gegen "${entry.sanctioned_name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/api/sanctionssystem/${entry.id}`);
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-100">Sanktionssystem</h1>
        <p className="text-sm text-gray-500 mt-1">
          Automatisch aus Discord synchronisierte Sanktionen.
        </p>
      </div>

      <SyncStatusCard onSynced={load} />

      {/* "Bezahlte Sanktionen": sichtbar für alle Rollen mit Zugriff auf
          dieses Modul - siehe Kommentar oben. */}
      <div className="flex gap-2 border-b border-surface-border overflow-x-auto">
        <button
          onClick={() => setView("open")}
          className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${view === "open" ? "border-accent text-accent" : "border-transparent text-gray-500 hover:text-gray-300"}`}
        >
          Offene Sanktionen
        </button>
        <button
          onClick={() => setView("paid")}
          className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${view === "paid" ? "border-accent text-accent" : "border-transparent text-gray-500 hover:text-gray-300"}`}
        >
          Bezahlte Sanktionen
        </button>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-100">{showPaidView ? "Bezahlte Sanktionen" : "Sanktionen"}</h2>
        <button className="btn-primary" onClick={() => setEditing(null)}>+ Hinzufügen</button>
      </div>

      {showPaidView && (
        <p className="text-xs text-gray-500">
          Bezahlte Sanktionen werden {PAID_CLEANUP_DAYS} Tage nach der Zahlung automatisch und endgültig gelöscht.
        </p>
      )}

      <Card padded={false} className="overflow-x-auto">
        {loading ? (
          <Spinner />
        ) : visibleEntries.length === 0 ? (
          <EmptyState message={showPaidView ? "Keine bezahlten Sanktionen." : "Keine Sanktionen erfasst."} />
        ) : (
            <table className="table-base">
              <thead>
                <tr>
                  <th>Sanktioniert</th>
                  <th>Grund</th>
                  <th>Betrag</th>
                  <th>Ersteller</th>
                  <th>Status</th>
                  <th>{showPaidView ? "Wird gelöscht am" : "Zu bezahlen bis"}</th>
                  <th>Erstellt</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visibleEntries.map((entry) => {
                  const due = dueDate(entry.created_at);
                  const isOverdue = entry.status !== "paid" && due.getTime() < Date.now();
                  return (
                  <tr key={entry.id}>
                    <td className="text-gray-100 font-medium whitespace-nowrap">{entry.sanctioned_name}</td>
                    <td className="text-gray-300">{entry.reason || "—"}</td>
                    <td className="text-gray-300 whitespace-nowrap">
                      {entry.amount !== null ? `${entry.amount.toLocaleString("de-DE")}${entry.currency_label ? ` ${entry.currency_label}` : ""}` : "—"}
                    </td>
                    <td className="text-gray-400 whitespace-nowrap">{entry.creator_name || "—"}</td>
                    <td><Badge tone={STATUS_TONE[entry.status]}>{STATUS_LABELS[entry.status]}</Badge></td>
                    {showPaidView ? (
                      <td className="whitespace-nowrap text-gray-300">
                        {entry.paid_at ? deletionDate(entry.paid_at).toLocaleDateString("de-DE") : "—"}
                      </td>
                    ) : (
                      <td className={`whitespace-nowrap ${isOverdue ? "text-danger font-semibold" : "text-gray-300"}`}>
                        {due.toLocaleDateString("de-DE")}
                        {isOverdue && <span className="block text-[11px] font-normal">Überfällig</span>}
                      </td>
                    )}
                    <td className="text-gray-500 text-xs whitespace-nowrap">
                      {formatDateTime(entry.created_at)}
                      {entry.discord_message_id && <span className="ml-1 text-accent" title="Automatisch aus Discord synchronisiert">🔄</span>}
                    </td>
                    <td className="whitespace-nowrap text-right">
                      <button className="text-accent text-xs hover:underline mr-3" onClick={() => setEditing(entry)}>Bearbeiten</button>
                      <button className="text-danger text-xs hover:underline" onClick={() => handleDelete(entry)}>Löschen</button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
        )}
      </Card>

      {editing !== undefined && (
        <EntryForm entry={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
      )}
    </div>
  );
}

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <span className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${ok ? "bg-success" : "bg-danger"}`} title={ok ? "Läuft fehlerfrei" : "Nicht eingerichtet oder mit Fehlern"} />
  );
}

function SyncStatusCard({ onSynced }: { onSynced: () => void }) {
  const [status, setStatus] = useState<SanctionssystemSyncStatus | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ tone: "success" | "danger"; text: string } | null>(null);

  const loadStatus = useCallback(async () => {
    setStatus(await api.get<SanctionssystemSyncStatus>("/api/sanctionssystem/sync-status"));
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  async function handleSync() {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const result = await api.post<{ processed: number }>("/api/sanctionssystem/sync-now");
      setSyncMessage({
        tone: "success",
        text: result.processed > 0
          ? `Synchronisierung abgeschlossen: ${result.processed} neue Meldung(en) verarbeitet.`
          : "Synchronisierung abgeschlossen: keine neuen Meldungen im Discord-Channel.",
      });
      loadStatus();
      onSynced();
    } catch (err) {
      setSyncMessage({ tone: "danger", text: err instanceof ApiError ? err.message : "Synchronisierung fehlgeschlagen." });
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div className="card p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
            <StatusDot ok={!!status?.configured && status.errorCount === 0} />
            Discord-Synchronisierung
          </p>
          <p className="text-sm text-gray-300 mt-2">
            {status?.configured ? "Automatisch, alle 60 Sekunden" : "Kein Channel eingerichtet"}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {status?.lastEventAt ? `Letzte Meldung: ${formatDateTime(status.lastEventAt)}` : "Noch keine Meldung empfangen"}
            {status && status.errorCount > 0 && <span className="text-danger"> · {status.errorCount} Fehler</span>}
          </p>
        </div>
        <button className="btn-primary whitespace-nowrap" onClick={handleSync} disabled={syncing}>
          {syncing ? "Synchronisiere…" : "🔄 Synchronisieren"}
        </button>
      </div>
      {syncMessage && (
        <div className={`text-xs rounded-lg px-3 py-2 ${syncMessage.tone === "success" ? "bg-success-muted text-success" : "bg-danger-muted text-danger"}`}>
          {syncMessage.text}
        </div>
      )}
    </div>
  );
}

function EntryForm({ entry, onClose, onSaved }: { entry: SanctionssystemEntry | null; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!entry;
  const [sanctionedName, setSanctionedName] = useState(entry?.sanctioned_name ?? "");
  const [creatorName, setCreatorName] = useState(entry?.creator_name ?? "");
  const [reason, setReason] = useState(entry?.reason ?? "");
  const [amount, setAmount] = useState(entry?.amount !== null && entry?.amount !== undefined ? String(entry.amount) : "");
  const [currencyLabel, setCurrencyLabel] = useState(entry?.currency_label ?? "");
  const [status, setStatus] = useState<SanctionssystemEntry["status"]>(entry?.status ?? "open");
  const [notes, setNotes] = useState(entry?.notes ?? "");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        sanctionedName, creatorName: creatorName || null, reason: reason || null,
        amount: amount.trim() ? Number(amount) : null, currencyLabel: currencyLabel || null,
        status, notes: notes || null,
      };
      if (isEdit) await api.put(`/api/sanctionssystem/${entry!.id}`, payload);
      else await api.post("/api/sanctionssystem", payload);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={isEdit ? "Sanktion bearbeiten" : "Neue Sanktion"}
      onClose={onClose}
      footer={<>
        <button className="btn-secondary" onClick={onClose} disabled={submitting}>Abbrechen</button>
        <button className="btn-primary" form="sanctionssystem-entry-form" disabled={submitting}>{submitting ? "Speichern…" : "Speichern"}</button>
      </>}
    >
      <form id="sanctionssystem-entry-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
        <div>
          <label className="label">Sanktionierte Person *</label>
          <input className="input" value={sanctionedName} onChange={(e) => setSanctionedName(e.target.value)} required autoFocus />
        </div>
        <div>
          <label className="label">Ersteller</label>
          <input className="input" value={creatorName} onChange={(e) => setCreatorName(e.target.value)} />
        </div>
        <div>
          <label className="label">Grund</label>
          <textarea className="input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Betrag</label>
            <input className="input" type="number" step="any" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div>
            <label className="label">Währung</label>
            <input className="input" value={currencyLabel} onChange={(e) => setCurrencyLabel(e.target.value)} placeholder="z.B. Grün" />
          </div>
        </div>
        <div>
          <label className="label">Status</label>
          <select className="input" value={status} onChange={(e) => setStatus(e.target.value as SanctionssystemEntry["status"])}>
            <option value="open">Offen</option>
            <option value="paid">Bezahlt</option>
            <option value="unpaid">Nicht bezahlt</option>
          </select>
        </div>
        <div>
          <label className="label">Notizen</label>
          <textarea className="input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </form>
    </Modal>
  );
}
